<template>
  <b-modal :active="active" @close="$emit('close')" has-modal-card trap-focus aria-role="dialog" aria-modal>
    <div class="modal-card" style="max-width: 520px; margin: auto;">
      <header class="modal-card-head">
        <p class="modal-card-title has-text-centered has-text-weight-bold">Clone Data</p>
      </header>
      <section class="modal-card-body">
        <!-- Level info banner -->
        <div class="level-banner mb-4">
          <div class="is-flex is-justify-content-space-between is-align-items-center">
            <span class="has-text-info is-size-7">Cloning at {{ levelLabel }} level</span>
            <b-icon icon="information" type="is-info" size="is-small"></b-icon>
          </div>
          <div class="mt-1">
            <span class="is-size-7">{{ entityLabel }}:</span>
            <span class="is-size-7 has-text-weight-bold"> {{ entityName }}</span>
          </div>
        </div>

        <!-- Data Type -->
        <b-field label="Data Type">
          <div class="is-flex is-flex-wrap-wrap" style="gap: 8px;">
            <b-checkbox v-for="dt in dataTypeOptions" :key="dt" v-model="form.dataType" :native-value="dt">
              {{ capitalizeFirst(dt) }}
            </b-checkbox>
          </div>
        </b-field>

        <!-- Destinations -->
        <b-field label="Destinations">
          <div class="is-flex is-flex-wrap-wrap" style="gap: 8px;">
            <b-checkbox v-for="dest in destinationOptions" :key="dest" v-model="form.destinations" :native-value="dest">
              {{ dest }}
            </b-checkbox>
          </div>
        </b-field>

        <!-- Processing Periods -->
        <div class="processing-periods-section mt-4">
          <div class="is-flex is-justify-content-space-between is-align-items-center mb-2">
            <div class="is-size-7 has-text-grey" style="text-transform: uppercase;">Processing Periods</div>
            <b-button size="is-small" type="is-success" icon-left="plus" @click="addPeriod">
              Add Period
            </b-button>
          </div>

          <div v-for="(period, pIndex) in form.processingPeriods" :key="'period-' + pIndex"
               class="period-block mb-3">
            <div class="is-flex is-justify-content-space-between is-align-items-center mb-2">
              <span class="is-size-7 has-text-weight-bold">Period {{ pIndex + 1 }}</span>
              <div class="is-flex" style="gap: 4px;">
                <b-button size="is-small" type="is-danger" icon-left="delete" outlined
                          @click="removePeriod(pIndex)">
                </b-button>
              </div>
            </div>

            <b-field label="Date Range">
              <b-datepicker v-model="period.dateRange" range icon="calendar-today" editable
                            :date-formatter="formatDateRange" placeholder="Select date range">
              </b-datepicker>
            </b-field>

            <!-- Time Ranges within this period -->
            <div class="is-size-7 has-text-grey mb-1" style="text-transform: uppercase;">Time Ranges</div>
            <div v-for="(tr, tIndex) in period.timeRanges" :key="'tr-' + pIndex + '-' + tIndex" class="mb-2">
              <div class="is-flex is-align-items-center" style="gap: 8px;">
                <b-field label="Start Time" expanded class="mb-0">
                  <b-timepicker v-model="tr.startTime" icon="clock" editable hour-format="24"
                                placeholder="Start time" size="is-small">
                  </b-timepicker>
                </b-field>
                <b-field label="End Time" expanded class="mb-0">
                  <b-timepicker v-model="tr.endTime" icon="clock" editable hour-format="24"
                                placeholder="End time" size="is-small">
                  </b-timepicker>
                </b-field>
                <div class="is-flex" style="gap: 4px; margin-top: 18px;">
                  <b-button size="is-small" type="is-danger" icon-left="delete" outlined
                            @click="removeTimeRange(pIndex, tIndex)">
                  </b-button>
                  <b-button size="is-small" type="is-success" icon-left="plus" outlined
                            @click="addTimeRange(pIndex)">
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- User Emails -->
        <b-field label="User Emails" class="mt-4">
          <b-taginput v-model="form.userEmails" icon="email" placeholder="Add email"
                      aria-close-label="Delete this email">
          </b-taginput>
        </b-field>
      </section>
      <footer class="modal-card-foot is-flex is-justify-content-flex-end" style="gap: 8px;">
        <b-button @click="$emit('close')">Cancel</b-button>
        <b-button type="is-info" icon-left="content-copy" @click="onClone"
                  :disabled="!form.dataType.length || !form.destinations.length">
          Clone
        </b-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: "clone-dialog",
  props: {
    active: {
      type: Boolean,
      default: false
    },
    level: {
      type: String,
      default: 'project',
      validator: (val) => ['project', 'site', 'dataDevice'].includes(val)
    },
    entityName: {
      type: String,
      default: ''
    },
    entityId: {
      type: String,
      default: ''
    },
    dataTypeOptions: {
      type: Array,
      default: () => []
    },
    destinationOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      form: {
        dataType: [],
        destinations: [],
        processingPeriods: [],
        userEmails: []
      }
    }
  },
  computed: {
    levelLabel() {
      if (this.level === 'dataDevice') return 'device'
      return this.level
    },
    entityLabel() {
      const labels = {
        project: 'Project name',
        site: 'Site name',
        dataDevice: 'Device name'
      }
      return labels[this.level] || 'Name'
    }
  },
  watch: {
    active(val) {
      if (val) {
        this.resetForm()
      }
    }
  },
  methods: {
    createEmptyTimeRange() {
      return { startTime: null, endTime: null }
    },
    createEmptyPeriod() {
      return {
        dateRange: [],
        timeRanges: [this.createEmptyTimeRange()]
      }
    },
    resetForm() {
      this.form.dataType = []
      this.form.destinations = []
      this.form.processingPeriods = [this.createEmptyPeriod()]
      this.form.userEmails = []
    },
    addPeriod() {
      this.form.processingPeriods.push(this.createEmptyPeriod())
    },
    removePeriod(pIndex) {
      if (this.form.processingPeriods.length === 1) {
        this.$set(this.form.processingPeriods, 0, this.createEmptyPeriod())
      } else {
        this.form.processingPeriods.splice(pIndex, 1)
      }
    },
    addTimeRange(pIndex) {
      this.form.processingPeriods[pIndex].timeRanges.push(this.createEmptyTimeRange())
    },
    removeTimeRange(pIndex, tIndex) {
      const period = this.form.processingPeriods[pIndex]
      if (period.timeRanges.length === 1) {
        this.$set(period.timeRanges, 0, this.createEmptyTimeRange())
      } else {
        period.timeRanges.splice(tIndex, 1)
      }
    },
    capitalizeFirst(str) {
      if (!str) return ''
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    formatDate(date) {
      if (!date) return ''
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    formatDateRange(dates) {
      if (!dates || dates.length < 2) return ''
      return `${this.formatDate(dates[0])} - ${this.formatDate(dates[1])}`
    },
    formatTime(date) {
      if (!date) return null
      const h = String(date.getHours()).padStart(2, '0')
      const m = String(date.getMinutes()).padStart(2, '0')
      return `${h}:${m}`
    },
    buildPayload() {
      const payload = {
        ids: [this.entityId],
        triggerBy: this.level,
        dataType: this.form.dataType,
        destinations: this.form.destinations,
        processingPeriods: [],
        userEmails: this.form.userEmails,
        deleteTask: false
      }

      for (const period of this.form.processingPeriods) {
        if (!period.dateRange || period.dateRange.length < 2) continue

        const built = {
          dateRange: [
            this.formatDate(period.dateRange[0]),
            this.formatDate(period.dateRange[1])
          ],
          timeRanges: []
        }

        for (const tr of period.timeRanges) {
          if (tr.startTime && tr.endTime) {
            built.timeRanges.push({
              startTime: this.formatTime(tr.startTime),
              endTime: this.formatTime(tr.endTime)
            })
          }
        }

        payload.processingPeriods.push(built)
      }

      return payload
    },
    onClone() {
      const payload = this.buildPayload()
      console.log('Clone payload:', payload)
      this.$emit('onClone', payload)
    }
  }
}
</script>

<style scoped lang="scss">
.level-banner {
  background-color: #f0f7ff;
  border: 1px solid #bee3f8;
  border-radius: 6px;
  padding: 10px 12px;
}

.processing-periods-section {
  border-top: 1px solid #eee;
  padding-top: 12px;
}

.period-block {
  background-color: #f9f9f9;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 12px;
}

.modal-card-foot {
  padding: 16px 20px;
}
</style>
